/**
 * Login Page
 *
 * Handles user authentication with email/password.
 * Redirects to /studio on successful login.
 */

import type { TenantId, Capability } from "@tatvex/types";
import { Loader2, Layers, AlertCircle } from "lucide-react";
import type { ReactElement, FormEvent } from "react";
import { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { apiRequest } from "../api/client";
import { useAuthStore } from "../store/authStore";

interface LoginResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly refreshToken?: string;
  readonly user: {
    readonly id: string;
    readonly email: string;
    readonly name: string;
  };
  readonly tenant: {
    readonly id: TenantId;
    readonly name: string;
  };
  readonly capabilities: readonly Capability[];
}

export function LoginPage(): ReactElement {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setUser, setTenant, setCapabilities, isAuthenticated } = useAuthStore();

  // Redirect if already authenticated
  if (isAuthenticated) {
    const from = (location.state as { from?: string })?.from ?? "/studio";
    navigate(from, { replace: true });
  }

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        const response = await apiRequest<LoginResponse>("/api/v1/auth/login", {
          method: "POST",
          body: { email, password },
        });

        if (response.success) {
          const { accessToken, expiresIn, refreshToken, user, tenant, capabilities } =
            response.data;

          // Convert expiresIn (duration seconds) to expiresAt (Unix timestamp seconds)
          const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;

          // Update auth store
          setToken(accessToken, expiresAt, refreshToken);
          setUser(user);
          setTenant(tenant.id, tenant.name);
          setCapabilities(capabilities);

          // Navigate to intended destination or dashboard
          const from = (location.state as { from?: string })?.from ?? "/studio";
          navigate(from, { replace: true });
        } else {
          setError(response.error.message);
        }
      } catch {
        setError("An unexpected error occurred. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, navigate, location.state, setToken, setUser, setTenant, setCapabilities]
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        {/* Logo and title */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Layers className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Tatvex Studio</h1>
          <p className="mt-2 text-foreground-muted">Sign in to your account</p>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md border border-error/50 bg-error/10 p-3 text-sm text-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              className="input"
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              className="input"
              disabled={isLoading}
            />
          </div>

          <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-foreground-muted">
          Configuration management for comparison platforms
        </p>
      </div>
    </div>
  );
}
